import React, { useState, useEffect } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Text, VStack, HStack, Select, Checkbox, Spinner,
  Box, Divider, Badge, Input, Table, Thead, Tbody, Tr, Th, Td, Accordion,
  AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Alert, AlertIcon,
  FormControl, FormLabel, FormHelperText, Link, RadioGroup, Radio, Stack, Textarea
} from "@chakra-ui/react";
import { getBackendURL } from "../../components/env";

export default function MigrationModal({ isOpen, onClose, onRun }) {
  // Navigation: 'menu' | 'connect' | 'projects' | 'tables' | 'pre_sync' | 'post_sync'
  //           | 'auth_options' | 'auth_pre_sync' | 'auth_post_sync'
  const [view, setView] = useState("menu");
  // Secrets typed into the wizard. Sent with every backend call, kept in memory
  // only, wiped whenever the modal opens — nothing is written to .env.
  const EMPTY_CREDS = {
    access_token: "",
    source_db_password: "",
    target_db_password: "",
    source_db_url: "",
    target_db_url: "",
  };
  const [creds, setCreds] = useState(EMPTY_CREDS);
  // 'token': list projects through the Management API (needs a personal access token)
  // 'connstr': paste each project's connection string from its Connect dialog (no token)
  const [connectMode, setConnectMode] = useState("token");
  // Which wizard owns the shared 'projects' step: 'tables' (non-PHI) | 'auth'
  const [wizard, setWizard] = useState("tables");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Projects
  const [projects, setProjects] = useState([]);
  const [sourceRef, setSourceRef] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [targetRef, setTargetRef] = useState("");
  const [targetName, setTargetName] = useState("");

  // Tables & Configurations
  const [tables, setTables] = useState([]);
  const [tableConfigs, setTableConfigs] = useState({});
  const [showAllTables, setShowAllTables] = useState(false);

  // Settings
  const [allowCreateTables, setAllowCreateTables] = useState(false);
  const [allowAlterTables, setAllowAlterTables] = useState(true);
  const [syncIndexesTriggers, setSyncIndexesTriggers] = useState(true);
  const [allowOverwrite, setAllowOverwrite] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  // Reports
  const [preSyncReport, setPreSyncReport] = useState(null);
  const [postSyncReport, setPostSyncReport] = useState(null);

  // Auth users & accounts wizard
  const [authOpts, setAuthOpts] = useState({
    include_auth: true,
    include_mfa: true,
    include_public_users: true,
    include_tenant_users: true,
    allow_create_tables: false,
    allow_overwrite: false,
  });
  const [authPlan, setAuthPlan] = useState(null);
  const [authSelected, setAuthSelected] = useState({});
  const [authReport, setAuthReport] = useState(null);

  const backendUrl = getBackendURL();

  useEffect(() => {
    if (isOpen) {
      setView("menu");
      setErrorMsg("");
      setPreSyncReport(null);
      setPostSyncReport(null);
      setAuthPlan(null);
      setAuthReport(null);
      setConfirmText("");
      setCreds(EMPTY_CREDS);
      setProjects([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleStartWizard = (nextWizard = "tables") => {
    setWizard(nextWizard);
    setErrorMsg("");
    setView("connect");
  };

  const handleUseConnectionStrings = () => {
    setErrorMsg("");
    setProjects([]);
    setSourceRef("manual:source");
    setSourceName(sourceName || "prod-sim2");
    setTargetRef("manual:target");
    setTargetName(targetName || "GoogleForms");
    setView("projects");
  };

  const handleLoadProjects = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await fetch(`${backendUrl}/api/migration/projects/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ access_token: creds.access_token }),
      });
      const data = await res.json();
      if (data.projects) {
        setProjects(data.projects);
        const prod = data.projects.find((p) => p.name.toLowerCase().includes("prod-sim2"));
        const forms = data.projects.find((p) => p.name.toLowerCase().includes("googleforms"));
        if (prod) {
          setSourceRef(prod.id);
          setSourceName(prod.name);
        }
        if (forms) {
          setTargetRef(forms.id);
          setTargetName(forms.name);
        }
        setView("projects");
      } else {
        setErrorMsg(data.error || "Failed to load projects");
      }
    } catch (err) {
      setErrorMsg("Failed to load projects: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchSchema = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await fetch(`${backendUrl}/api/migration/schema/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_ref: sourceRef,
          project_name: sourceName,
          access_token: creds.access_token,
          source_db_password: creds.source_db_password,
        }),
      });
      const data = await res.json();
      if (data.tables) {
        setTables(data.tables);
        const initialConfigs = {};
        data.tables.forEach((t) => {
          initialConfigs[t.name] = {
            selected: t.is_default,
            columns: t.default_selected_columns || t.all_columns,
            conflict_key: t.candidate_keys.length > 0 ? [t.candidate_keys[0]] : ["id"],
            filter_column: t.date_columns[0] || "",
            filter_mode: "all",
            single_date: "",
            range_start: "",
            range_end: "",
            multi_dates: "",
          };
        });
        setTableConfigs(initialConfigs);
        setView("tables");
      } else {
        setErrorMsg(data.error || "Failed to load schema");
      }
    } catch (err) {
      setErrorMsg("Failed to load schema: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper to build the payload for both dry run and live run
  const buildPayload = (isDryRun, confirmation = "") => {
    const payloadTables = Object.keys(tableConfigs)
      .filter((name) => tableConfigs[name].selected)
      .map((name) => {
        const cfg = tableConfigs[name];
        let filter_dates = [];
        if (cfg.filter_mode === "single" && cfg.single_date) {
          filter_dates = [cfg.single_date];
        } else if (cfg.filter_mode === "range" && cfg.range_start && cfg.range_end) {
          filter_dates = [cfg.range_start, cfg.range_end];
        } else if (cfg.filter_mode === "multiple" && cfg.multi_dates) {
          filter_dates = cfg.multi_dates.split(",").map((s) => s.trim()).filter(Boolean);
        }

        return {
          name,
          columns: cfg.columns,
          conflict_key: cfg.conflict_key,
          filter_column: cfg.filter_column,
          filter_mode: cfg.filter_mode,
          filter_dates,
        };
      });

    return {
      source_ref: sourceRef,
      source_name: sourceName,
      target_ref: targetRef,
      target_name: targetName,
      tables: payloadTables,
      ...creds,
      dry_run: isDryRun,
      allow_create_tables: allowCreateTables,
      allow_alter_tables: allowAlterTables,
      sync_indexes_triggers: syncIndexesTriggers,
      allow_overwrite: allowOverwrite,
      confirm_text: confirmation,
    };
  };

  // Step 2 -> Step 3: Run read-only Pre-Sync analysis
  const handleGeneratePreSyncPlan = async () => {
    setLoading(true);
    setErrorMsg("");
    const body = buildPayload(true);

    try {
      const result = await onRun(body);
      if (result.error) {
        setErrorMsg(result.error);
      } else {
        setPreSyncReport(result.pre_sync_report || []);
        setView("pre_sync");
      }
    } catch (err) {
      setErrorMsg(err.message || "Failed to generate Pre-Sync plan");
    } finally {
      setLoading(false);
    }
  };

  // Step 3 -> Step 4: Run the actual Live Sync
  const handleExecuteLiveSync = async () => {
    if (confirmText.trim() !== "CONFIRM") {
      setErrorMsg("You must type CONFIRM to execute live database writes.");
      return;
    }

    setLoading(true);
    setErrorMsg("");
    const body = buildPayload(false, confirmText);

    try {
      const result = await onRun(body);
      if (result.error) {
        setErrorMsg(result.error);
      } else {
        setPostSyncReport(result.post_sync_report || []);
        setView("post_sync");
      }
    } catch (err) {
      setErrorMsg(err.message || "Live sync failed");
    } finally {
      setLoading(false);
    }
  };

  // Auth users & accounts: one endpoint, dry_run decides plan vs. live copy
  const runAuthMigration = async (isDryRun, confirmation = "") => {
    const selectedNames = Object.keys(authSelected).filter((n) => authSelected[n]);
    const res = await fetch(`${backendUrl}/api/migration/auth/run/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        source_ref: sourceRef,
        source_name: sourceName,
        target_ref: targetRef,
        target_name: targetName,
        dry_run: isDryRun,
        confirm_text: confirmation,
        ...creds,
        ...authOpts,
        ...(isDryRun ? {} : { tables: selectedNames }),
      }),
    });
    return res.json();
  };

  const handleAuthPlan = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const result = await runAuthMigration(true);
      if (result.error) {
        setErrorMsg(result.error);
      } else {
        const plan = result.pre_sync_report || [];
        setAuthPlan(plan);
        const sel = {};
        plan.forEach((r) => {
          sel[r.name] = r.planned_action !== "BLOCKED" && r.planned_action !== "SKIP";
        });
        setAuthSelected(sel);
        setConfirmText("");
        setView("auth_pre_sync");
      }
    } catch (err) {
      setErrorMsg(err.message || "Failed to build the auth migration plan");
    } finally {
      setLoading(false);
    }
  };

  const handleAuthExecute = async () => {
    if (confirmText.trim() !== "CONFIRM") {
      setErrorMsg("You must type CONFIRM to execute live database writes.");
      return;
    }
    setLoading(true);
    setErrorMsg("");
    try {
      const result = await runAuthMigration(false, confirmText);
      if (result.error) {
        setErrorMsg(result.error);
      } else {
        setAuthReport(result.post_sync_report || []);
        setView("auth_post_sync");
      }
    } catch (err) {
      setErrorMsg(err.message || "Auth migration failed");
    } finally {
      setLoading(false);
    }
  };

  const authOptionRows = [
    ["include_auth", "auth.users + auth.identities (logins, password hashes, Google identities)"],
    ["include_mfa", "auth.mfa_factors (TOTP enrolments)"],
    ["include_public_users", "public.users (schema placement, global role)"],
    ["include_tenant_users", "<tenant>.users for every tenant schema (per-schema roles)"],
  ];
  const authSafetyRows = [
    ["allow_create_tables", "Allow creating missing account tables/schemas on target (never auth.*)"],
    ["allow_overwrite", "Overwrite rows that already exist on the target (same id)"],
  ];

  const displayedTables = showAllTables ? tables : tables.filter((t) => t.is_default);
  const manualMode = connectMode === "connstr";
  const urlNeedsPassword = (url) => !url.trim() || url.includes("[YOUR-PASSWORD]");
  const projectsReady = manualMode
    ? !!creds.source_db_url.trim() && !!creds.target_db_url.trim() &&
      creds.source_db_url.trim() !== creds.target_db_url.trim() &&
      (!urlNeedsPassword(creds.source_db_url) || !!creds.source_db_password.trim()) &&
      (!urlNeedsPassword(creds.target_db_url) || !!creds.target_db_password.trim())
    : !!sourceRef && !!targetRef && sourceRef !== targetRef &&
      !!creds.source_db_password.trim() && !!creds.target_db_password.trim();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent maxH="90vh" overflowY="auto">
        <ModalHeader>
          {view === "menu" && "Database Migrations"}
          {view === "connect" && "Step 0: Connect to Supabase"}
          {view === "projects" && "Step 1: Choose Source & Target"}
          {view === "tables" && "Step 2: Configure Tables & Safeguards"}
          {view === "pre_sync" && "Step 3: Pre-Sync Plan (Review Before Running)"}
          {view === "post_sync" && "Step 4: Post-Sync Results"}
          {view === "auth_options" && "Step 2: Auth Users & Accounts — What to Copy"}
          {view === "auth_pre_sync" && "Step 3: Auth Migration Plan (Review Before Running)"}
          {view === "auth_post_sync" && "Step 4: Auth Migration Results"}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={4}>
          {errorMsg && (
            <Alert status="error" mb={4} borderRadius="md">
              <AlertIcon />
              {errorMsg}
            </Alert>
          )}

          {loading && <Spinner display="block" mx="auto" my={8} />}

          {/* VIEW: Menu */}
          {!loading && view === "menu" && (
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm" color="gray.600">
                Select a migration script to run:
              </Text>
              <Button
                colorScheme="teal"
                alignSelf="flex-start"
                data-testid="run-non-phi-migration"
                onClick={() => handleStartWizard("tables")}
              >
                Run non-PHI migration script
              </Button>
              <Button
                colorScheme="purple"
                alignSelf="flex-start"
                data-testid="run-auth-migration"
                onClick={() => handleStartWizard("auth")}
              >
                Migrate auth users &amp; accounts
              </Button>
              <Text fontSize="xs" color="gray.500">
                Copies auth.users / identities / MFA plus public.users and every tenant users
                table from the source project to the target, keeping ids and password hashes.
              </Text>
            </VStack>
          )}

          {/* VIEW: Connect (secrets typed here, never read from .env) */}
          {!loading && view === "connect" && (
            <VStack spacing={4} align="stretch">
              <Alert status="info" borderRadius="md" fontSize="sm">
                <AlertIcon />
                What you type here is only used for this run and forgotten when the window closes.
              </Alert>
              <RadioGroup value={connectMode} onChange={setConnectMode}>
                <Stack spacing={2}>
                  <Radio value="token" data-testid="mode-token">
                    I have a Supabase access token (lists the projects for me)
                  </Radio>
                  <Radio value="connstr" data-testid="mode-connstr">
                    I'll paste each project's connection string (no token needed)
                  </Radio>
                </Stack>
              </RadioGroup>
              {!manualMode ? (
                <FormControl isRequired>
                  <FormLabel>Supabase access token</FormLabel>
                  <Input
                    type="password"
                    autoComplete="off"
                    placeholder="sbp_…"
                    data-testid="cred-access-token"
                    value={creds.access_token}
                    onChange={(e) => setCreds((c) => ({ ...c, access_token: e.target.value }))}
                  />
                  <FormHelperText>
                    Made under <b>your own</b> account (any org member can): dashboard → avatar
                    (bottom-left) → Account → Access Tokens → Generate new token.{" "}
                    <Link href="https://supabase.com/dashboard/account/tokens" isExternal color="teal.600">
                      Open the tokens page
                    </Link>
                  </FormHelperText>
                </FormControl>
              ) : (
                <Text fontSize="sm" color="gray.600">
                  On the next screen, paste each project's <b>Session pooler</b> connection string:
                  open the project in Supabase → <b>Connect</b> (top bar) → <b>Session pooler</b> → copy.
                  Leave <code>[YOUR-PASSWORD]</code> in it and type the password in the box underneath.
                </Text>
              )}
            </VStack>
          )}

          {/* VIEW: Projects — connection-string mode */}
          {!loading && view === "projects" && manualMode && (
            <VStack spacing={4} align="stretch">
              {[
                ["source", "Source project (copy FROM)", sourceName, setSourceName, "source_db_url", "source_db_password", "cred-source-url", "cred-source-password"],
                ["target", "Target project (copy TO)", targetName, setTargetName, "target_db_url", "target_db_password", "cred-target-url", "cred-target-password"],
              ].map(([key, title, name, setName, urlKey, pwKey, urlTestId, pwTestId]) => (
                <Box key={key} borderWidth="1px" borderRadius="md" p={3}>
                  <Text fontWeight="semibold" mb={2}>{title}</Text>
                  <Input
                    size="sm"
                    mb={2}
                    placeholder="Project name (label only)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <Textarea
                    size="sm"
                    rows={2}
                    fontFamily="mono"
                    placeholder="postgresql://postgres.xxxx:[YOUR-PASSWORD]@aws-0-region.pooler.supabase.com:5432/postgres"
                    data-testid={urlTestId}
                    value={creds[urlKey]}
                    onChange={(e) => setCreds((c) => ({ ...c, [urlKey]: e.target.value }))}
                  />
                  {urlNeedsPassword(creds[urlKey]) && (
                    <Input
                      mt={2}
                      size="sm"
                      type="password"
                      autoComplete="off"
                      placeholder={`Database password for ${name || key}`}
                      data-testid={pwTestId}
                      value={creds[pwKey]}
                      onChange={(e) => setCreds((c) => ({ ...c, [pwKey]: e.target.value }))}
                    />
                  )}
                </Box>
              ))}
              {creds.source_db_url.trim() && creds.source_db_url.trim() === creds.target_db_url.trim() && (
                <Alert status="error" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  Source and target are the same connection string.
                </Alert>
              )}
            </VStack>
          )}

          {/* VIEW: Projects */}
          {!loading && view === "projects" && !manualMode && (
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Pick the two projects, then paste each project's database password
                (Supabase → that project → Project Settings → Database → Database password).
              </Text>
              <Box>
                <Text fontWeight="semibold" mb={1}>Source Database:</Text>
                <Select
                  value={sourceRef}
                  onChange={(e) => {
                    const p = projects.find((x) => x.id === e.target.value);
                    if (p) {
                      setSourceRef(p.id);
                      setSourceName(p.name);
                    }
                  }}
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.region})</option>
                  ))}
                </Select>
                <Input
                  mt={2}
                  type="password"
                  autoComplete="off"
                  placeholder={`Database password for ${sourceName || "the source project"}`}
                  data-testid="cred-source-password"
                  value={creds.source_db_password}
                  onChange={(e) => setCreds((c) => ({ ...c, source_db_password: e.target.value }))}
                />
              </Box>

              <Box>
                <Text fontWeight="semibold" mb={1}>Target Database:</Text>
                <Select
                  value={targetRef}
                  onChange={(e) => {
                    const p = projects.find((x) => x.id === e.target.value);
                    if (p) {
                      setTargetRef(p.id);
                      setTargetName(p.name);
                    }
                  }}
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.region})</option>
                  ))}
                </Select>
                <Input
                  mt={2}
                  type="password"
                  autoComplete="off"
                  placeholder={`Database password for ${targetName || "the target project"}`}
                  data-testid="cred-target-password"
                  value={creds.target_db_password}
                  onChange={(e) => setCreds((c) => ({ ...c, target_db_password: e.target.value }))}
                />
              </Box>
              {sourceRef && sourceRef === targetRef && (
                <Alert status="error" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  Source and target are the same project.
                </Alert>
              )}
            </VStack>
          )}

          {/* VIEW: Tables & Configuration */}
          {!loading && view === "tables" && (
            <VStack spacing={4} align="stretch">
              <Accordion allowMultiple>
                {displayedTables.map((t) => {
                  const cfg = tableConfigs[t.name] || {};
                  return (
                    <AccordionItem key={t.name} borderWidth="1px" borderRadius="md" mb={2}>
                      <h2>
                        <AccordionButton>
                          <Checkbox
                            isChecked={cfg.selected || false}
                            mr={3}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setTableConfigs((prev) => ({
                                ...prev,
                                [t.name]: { ...prev[t.name], selected: checked },
                              }));
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Box as="span" flex="1" textAlign="left" fontWeight="bold">
                            {t.name} {t.is_default && <Badge colorScheme="purple">Default</Badge>}
                          </Box>
                          <Text fontSize="xs" color="gray.500" mr={3}>
                            {cfg.columns?.length || 0} / {t.all_columns.length} columns
                          </Text>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4}>
                        <Text fontSize="sm" fontWeight="bold" mb={1}>Columns to Sync:</Text>
                        <Box maxH="120px" overflowY="auto" borderWidth="1px" p={2} borderRadius="md" mb={3}>
                          {t.all_columns.map((c) => {
                            const isNotNull = t.not_null_columns.includes(c);
                            const isChecked = cfg.columns?.includes(c);
                            return (
                              <Checkbox
                                key={c}
                                mr={3}
                                mb={1}
                                isChecked={isChecked || isNotNull}
                                isDisabled={isNotNull}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setTableConfigs((prev) => {
                                    const current = prev[t.name].columns || [];
                                    const updated = checked
                                      ? [...current, c]
                                      : current.filter((x) => x !== c);
                                    return {
                                      ...prev,
                                      [t.name]: { ...prev[t.name], columns: updated },
                                    };
                                  });
                                }}
                              >
                                <Text fontSize="xs" as="span">
                                  {c} {isNotNull && "(NOT NULL)"}
                                </Text>
                              </Checkbox>
                            );
                          })}
                        </Box>

                        <Text fontSize="sm" fontWeight="bold" mb={1}>Date Filtering:</Text>
                        <HStack spacing={2} mb={2}>
                          <Select
                            size="sm"
                            value={cfg.filter_column || ""}
                            onChange={(e) =>
                              setTableConfigs((prev) => ({
                                ...prev,
                                [t.name]: { ...prev[t.name], filter_column: e.target.value },
                              }))
                            }
                          >
                            <option value="">(No date filter column)</option>
                            {t.date_columns.map((dc) => (
                              <option key={dc} value={dc}>{dc}</option>
                            ))}
                          </Select>

                          <Select
                            size="sm"
                            value={cfg.filter_mode}
                            onChange={(e) =>
                              setTableConfigs((prev) => ({
                                ...prev,
                                [t.name]: { ...prev[t.name], filter_mode: e.target.value },
                              }))
                            }
                          >
                            <option value="all">All Rows</option>
                            <option value="single">Single Date</option>
                            <option value="range">Date Range</option>
                            <option value="multiple">Multiple Dates</option>
                          </Select>
                        </HStack>

                        {cfg.filter_mode === "single" && (
                          <Input
                            size="sm"
                            type="date"
                            value={cfg.single_date}
                            onChange={(e) =>
                              setTableConfigs((prev) => ({
                                ...prev,
                                [t.name]: { ...prev[t.name], single_date: e.target.value },
                              }))
                            }
                          />
                        )}

                        {cfg.filter_mode === "range" && (
                          <HStack>
                            <Input
                              size="sm"
                              type="date"
                              placeholder="Start Date"
                              value={cfg.range_start}
                              onChange={(e) =>
                                setTableConfigs((prev) => ({
                                  ...prev,
                                  [t.name]: { ...prev[t.name], range_start: e.target.value },
                                }))
                              }
                            />
                            <Input
                              size="sm"
                              type="date"
                              placeholder="End Date"
                              value={cfg.range_end}
                              onChange={(e) =>
                                setTableConfigs((prev) => ({
                                  ...prev,
                                  [t.name]: { ...prev[t.name], range_end: e.target.value },
                                }))
                              }
                            />
                          </HStack>
                        )}

                        {cfg.filter_mode === "multiple" && (
                          <Input
                            size="sm"
                            placeholder="YYYY-MM-DD, YYYY-MM-DD, ..."
                            value={cfg.multi_dates}
                            onChange={(e) =>
                              setTableConfigs((prev) => ({
                                ...prev,
                                [t.name]: { ...prev[t.name], multi_dates: e.target.value },
                              }))
                            }
                          />
                        )}
                      </AccordionPanel>
                    </AccordionItem>
                  );
                })}
              </Accordion>

              <Button
                variant="link"
                size="sm"
                alignSelf="flex-start"
                onClick={() => setShowAllTables(!showAllTables)}
              >
                {showAllTables ? "Show Default Tables Only" : "Show More Tables"}
              </Button>

              <Divider />

              <Text fontWeight="bold">Safety Settings</Text>
              <Checkbox
                isChecked={allowCreateTables}
                onChange={(e) => setAllowCreateTables(e.target.checked)}
              >
                Allow creating missing tables on target
              </Checkbox>
              <Checkbox
                isChecked={allowAlterTables}
                onChange={(e) => setAllowAlterTables(e.target.checked)}
              >
                Allow ALTER TABLE to add missing columns on target
              </Checkbox>
              <Checkbox
                isChecked={syncIndexesTriggers}
                onChange={(e) => setSyncIndexesTriggers(e.target.checked)}
              >
                Sync missing indexes, triggers, and pg_trgm
              </Checkbox>
              <Checkbox
                isChecked={allowOverwrite}
                onChange={(e) => setAllowOverwrite(e.target.checked)}
              >
                Allow overwriting existing target rows (Upsert)
              </Checkbox>
            </VStack>
          )}

          {/* VIEW: Pre-Sync Plan (Read-Only Preview) */}
          {!loading && view === "pre_sync" && (
            <VStack spacing={4} align="stretch">
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                Review the plan below. No data has been modified.
              </Alert>

              <Box overflowX="auto" borderWidth="1px" borderRadius="md">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Table</Th>
                      <Th>Action</Th>
                      <Th isNumeric>Source Matches</Th>
                      <Th isNumeric>Target Rows</Th>
                      <Th>Note</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {preSyncReport?.map((r) => (
                      <Tr key={r.table}>
                        <Td fontWeight="semibold">{r.table}</Td>
                        <Td>
                          <Badge
                            colorScheme={
                              r.planned_action === "BLOCKED"
                                ? "red"
                                : r.planned_action.includes("CREATE")
                                ? "blue"
                                : "green"
                            }
                          >
                            {r.planned_action}
                          </Badge>
                        </Td>
                        <Td isNumeric>{r.source_rows}</Td>
                        <Td isNumeric>{r.target_rows}</Td>
                        <Td fontSize="xs" color="gray.600">{r.note || "-"}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              <Box bg="orange.50" p={3} borderRadius="md" borderWidth="1px">
                <Text fontSize="xs" fontWeight="bold" color="orange.800" mb={1}>
                  To proceed with the live sync, type CONFIRM below:
                </Text>
                <Input
                  size="sm"
                  bg="white"
                  placeholder="CONFIRM"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                />
              </Box>
            </VStack>
          )}

          {/* VIEW: Post-Sync Results (Shown Only After Live Run) */}
          {!loading && view === "post_sync" && (
            <VStack spacing={4} align="stretch">
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                Live sync completed successfully!
              </Alert>

              <Box overflowX="auto" borderWidth="1px" borderRadius="md">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Table</Th>
                      <Th>Status</Th>
                      <Th isNumeric>Source Processed</Th>
                      <Th isNumeric>Rows Before</Th>
                      <Th isNumeric>Rows After</Th>
                      <Th isNumeric>Net New</Th>
                      <Th isNumeric>Seconds</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {postSyncReport?.map((r) => (
                      <Tr key={r.table}>
                        <Td fontWeight="semibold">{r.table}</Td>
                        <Td>
                          <Badge colorScheme={r.status === "synced" ? "green" : "orange"}>
                            {r.status}
                          </Badge>
                        </Td>
                        <Td isNumeric>{r.rows_processed}</Td>
                        <Td isNumeric>{r.rows_before ?? "-"}</Td>
                        <Td isNumeric>{r.rows_after ?? "-"}</Td>
                        <Td isNumeric>{r.net_new_rows ?? 0}</Td>
                        <Td isNumeric>{r.seconds ? `${r.seconds}s` : "-"}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </VStack>
          )}

          {/* VIEW: Auth options */}
          {!loading && view === "auth_options" && (
            <VStack spacing={3} align="stretch">
              <Alert status="info" borderRadius="md" fontSize="sm">
                <AlertIcon />
                Rows are copied straight from Postgres, so user ids, bcrypt password hashes,
                OAuth identities and metadata survive. Sessions are not copied — everyone
                signs in again on the new project.
              </Alert>
              <Text fontWeight="bold">Tables</Text>
              {authOptionRows.map(([key, label]) => (
                <Checkbox
                  key={key}
                  isChecked={authOpts[key]}
                  onChange={(e) => setAuthOpts((prev) => ({ ...prev, [key]: e.target.checked }))}
                >
                  {label}
                </Checkbox>
              ))}
              <Divider />
              <Text fontWeight="bold">Safety Settings</Text>
              {authSafetyRows.map(([key, label]) => (
                <Checkbox
                  key={key}
                  isChecked={authOpts[key]}
                  onChange={(e) => setAuthOpts((prev) => ({ ...prev, [key]: e.target.checked }))}
                >
                  {label}
                </Checkbox>
              ))}
            </VStack>
          )}

          {/* VIEW: Auth plan */}
          {!loading && view === "auth_pre_sync" && (
            <VStack spacing={4} align="stretch">
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                Review the plan below. No data has been modified. Untick a row to leave it out.
              </Alert>

              <Box overflowX="auto" borderWidth="1px" borderRadius="md">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th></Th>
                      <Th>Table</Th>
                      <Th>Action</Th>
                      <Th isNumeric>Source</Th>
                      <Th isNumeric>Target</Th>
                      <Th isNumeric>New</Th>
                      <Th isNumeric>Existing</Th>
                      <Th>Note</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {authPlan?.map((r) => {
                      const blocked = r.planned_action === "BLOCKED" || r.planned_action === "SKIP";
                      return (
                        <Tr key={r.name}>
                          <Td>
                            <Checkbox
                              isChecked={!!authSelected[r.name]}
                              isDisabled={blocked}
                              onChange={(e) =>
                                setAuthSelected((prev) => ({ ...prev, [r.name]: e.target.checked }))
                              }
                            />
                          </Td>
                          <Td fontWeight="semibold">{r.name}</Td>
                          <Td>
                            <Badge
                              colorScheme={
                                r.planned_action === "BLOCKED"
                                  ? "red"
                                  : r.planned_action === "SKIP"
                                  ? "gray"
                                  : r.planned_action.includes("CREATE")
                                  ? "blue"
                                  : "green"
                              }
                            >
                              {r.planned_action}
                            </Badge>
                          </Td>
                          <Td isNumeric>{r.source_rows}</Td>
                          <Td isNumeric>{r.target_exists ? r.target_rows : "-"}</Td>
                          <Td isNumeric>{r.new_rows}</Td>
                          <Td isNumeric>{r.existing_rows}</Td>
                          <Td fontSize="xs" color="gray.600">
                            {r.note || "-"}
                            {r.email_conflicts?.length > 0 && (
                              <Box mt={1} color="orange.700">
                                {r.email_conflicts.join(", ")}
                                {r.email_conflict_count > r.email_conflicts.length && " …"}
                              </Box>
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>

              <Box bg="orange.50" p={3} borderRadius="md" borderWidth="1px">
                <Text fontSize="xs" fontWeight="bold" color="orange.800" mb={1}>
                  To copy the selected tables into {targetName || "the target"}, type CONFIRM below:
                </Text>
                <Input
                  size="sm"
                  bg="white"
                  placeholder="CONFIRM"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                />
              </Box>
            </VStack>
          )}

          {/* VIEW: Auth results */}
          {!loading && view === "auth_post_sync" && (
            <VStack spacing={4} align="stretch">
              <Alert
                status={authReport?.some((r) => r.status === "failed") ? "warning" : "success"}
                borderRadius="md"
              >
                <AlertIcon />
                {authReport?.some((r) => r.status === "failed")
                  ? "Finished with failures — see the notes."
                  : "Auth users and accounts copied."}
              </Alert>

              <Box overflowX="auto" borderWidth="1px" borderRadius="md">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Table</Th>
                      <Th>Status</Th>
                      <Th isNumeric>Processed</Th>
                      <Th isNumeric>Inserted</Th>
                      <Th isNumeric>Updated</Th>
                      <Th isNumeric>Email-skipped</Th>
                      <Th isNumeric>Rows After</Th>
                      <Th>Note</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {authReport?.map((r) => (
                      <Tr key={r.table}>
                        <Td fontWeight="semibold">{r.table}</Td>
                        <Td>
                          <Badge
                            colorScheme={
                              r.status === "synced" ? "green" : r.status === "failed" ? "red" : "orange"
                            }
                          >
                            {r.status}
                          </Badge>
                        </Td>
                        <Td isNumeric>{r.rows_processed}</Td>
                        <Td isNumeric>{r.inserted ?? "-"}</Td>
                        <Td isNumeric>{r.updated ?? "-"}</Td>
                        <Td isNumeric>{r.email_skipped ?? "-"}</Td>
                        <Td isNumeric>{r.rows_after ?? "-"}</Td>
                        <Td fontSize="xs" color="gray.600">{r.note || "-"}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                Next: point the app at the target project (SUPABASE_URL / keys / JWT secret on
                the backend, REACT_APP_SUPABASE_* on the frontend, RDS .env) and re-add the Google
                OAuth redirect URL and email templates on the target — those live in project
                settings, not in the database.
              </Alert>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          {view === "connect" && (
            <>
              <Button variant="ghost" mr={3} onClick={() => setView("menu")}>
                Back to Menu
              </Button>
              {manualMode ? (
                <Button colorScheme="teal" onClick={handleUseConnectionStrings} data-testid="use-connstr">
                  Next: Paste Connection Strings
                </Button>
              ) : (
                <Button
                  colorScheme="teal"
                  onClick={handleLoadProjects}
                  isLoading={loading}
                  isDisabled={!creds.access_token.trim()}
                  data-testid="load-projects"
                >
                  Load Projects
                </Button>
              )}
            </>
          )}

          {view === "projects" && (
            <>
              <Button variant="ghost" mr={3} onClick={() => setView("connect")}>
                Back
              </Button>
              {wizard === "auth" ? (
                <Button
                  colorScheme="purple"
                  onClick={() => setView("auth_options")}
                  isLoading={loading}
                  isDisabled={!projectsReady}
                >
                  Next: Choose What to Copy
                </Button>
              ) : (
                <Button
                  colorScheme="teal"
                  onClick={handleFetchSchema}
                  isLoading={loading}
                  isDisabled={!projectsReady}
                >
                  Next: Configure Tables
                </Button>
              )}
            </>
          )}

          {view === "auth_options" && (
            <>
              <Button variant="ghost" mr={3} onClick={() => setView("projects")}>
                Back
              </Button>
              <Button colorScheme="blue" onClick={handleAuthPlan} isLoading={loading}>
                Review Migration Plan
              </Button>
            </>
          )}

          {view === "auth_pre_sync" && (
            <>
              <Button variant="ghost" mr="auto" onClick={() => setView("auth_options")}>
                Back
              </Button>
              <Button
                colorScheme="red"
                onClick={handleAuthExecute}
                isLoading={loading}
                isDisabled={
                  confirmText.trim() !== "CONFIRM" ||
                  !Object.values(authSelected).some(Boolean)
                }
              >
                Copy Users to Target
              </Button>
            </>
          )}

          {view === "auth_post_sync" && (
            <Button colorScheme="teal" onClick={onClose}>
              Done
            </Button>
          )}

          {view === "tables" && (
            <>
              <Button variant="ghost" mr={3} onClick={() => setView("projects")}>
                Back
              </Button>
              <Button colorScheme="blue" onClick={handleGeneratePreSyncPlan} isLoading={loading}>
                Review Pre-Sync Plan
              </Button>
            </>
          )}

          {view === "pre_sync" && (
            <>
              <Button variant="ghost" mr="auto" onClick={() => setView("tables")}>
                Back to Configure Tables
              </Button>
              <Button
                colorScheme="red"
                onClick={handleExecuteLiveSync}
                isLoading={loading}
                isDisabled={confirmText.trim() !== "CONFIRM"}
              >
                Execute Live Sync
              </Button>
            </>
          )}

          {view === "post_sync" && (
            <Button colorScheme="teal" onClick={onClose}>
              Done
            </Button>
          )}

          {view === "menu" && (
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
